import { type ApiResponse, ApiFactory } from '@/utils/api'
import type {
  BaseCreateStoreReqType,
  BaseChangeStoreGroupReqType,
  BaseStoreInfoV2Req,
  StoreTimeZoneGroupResType
} from '@/models/types/store.types'
import type {
  StoreOperationsAnalyticsResType,
  GetStoreOperationsAnalyticsReqType
} from '@/models/types/storeOperationsAnalytics.types'
import { StoreEnums } from '@/constants/enums/api/store.enums'
import { SETTLEMENT_TIMEZONES } from '@/constants/settlement-timezones'
/**
 * 後端的時區是從 timeZoneOffset 反推出來的 IANA，同一個偏移量有多個城市（例如 UTC+8 可能
 * 回 Asia/Shanghai），很容易落在 SETTLEMENT_TIMEZONES 這 27 筆之外。沿用後端 label 的話
 * 那些選項就沒有「(UTC+X)」前綴，時區清單看起來參差不齊；這裡直接用 IANA 算出實際偏移量補上。
 * 偏移量不是整點時（例如印度 +5:30）會顯示到分鐘。
 *
 * 用原生 Intl 而不是 dayjs：資料層不該把 dayjs 與它的 plugin 初始化帶進每個 import 這支
 * service 的地方（dayjs 的 timezone plugin 底層同樣是 Intl，結果一致）。
 */
const formatUtcOffsetLabel = (timeZoneId: string, fallbackLabel: string): string => {
  try {
    // longOffset 會回 "GMT+08:00"、"GMT+05:30" 這種格式
    const gmtOffset = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZoneId,
      timeZoneName: 'longOffset'
    })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value

    const matched = gmtOffset?.match(/^GMT([+-])(\d{2}):(\d{2})$/)
    if (!matched) return fallbackLabel

    const [, sign, hours, minutes] = matched
    const offsetText = minutes === '00' ? `${Number(hours)}` : `${Number(hours)}:${minutes}`
    return `(UTC${sign}${offsetText}) ${timeZoneId}`
  } catch {
    // 無法辨識的時區會讓 Intl 拋 RangeError，這種時候保留後端原本的字串
    return fallbackLabel
  }
}

export class StoreServices extends ApiFactory {
  constructor() {
    super('')
  }

  /** 取得所有店家資訊清單 */
  getStoresListInfo = async (
    params: {
      startDate?: string
      endDate?: string
    },
    abortSignal?: AbortSignal
  ): Promise<ApiResponse> => {
    return await this.get<unknown, ApiResponse>(StoreEnums.stores, params, {
      signal: abortSignal
    })
  }

  /** 新系統：依選定時區查詢此帳號底下所有店家在指定日期範圍內的每日帳務聚合 */
  getStoresListInfoV2 = async (
    params: BaseStoreInfoV2Req,
    abortSignal?: AbortSignal
  ): Promise<ApiResponse> => {
    return await this.get<unknown, ApiResponse>(StoreEnums.storesV2, params, {
      signal: abortSignal
    })
  }

  /** 新系統：一次取得此帳號底下所有店家的營運分析與風險警示計數（以 storeId 為 key） */
  getStoreOperationsAnalytics = async (
    params: GetStoreOperationsAnalyticsReqType
  ): Promise<ApiResponse<StoreOperationsAnalyticsResType>> => {
    return await this.get<
      GetStoreOperationsAnalyticsReqType,
      ApiResponse<StoreOperationsAnalyticsResType>
    >(StoreEnums.storeOperationsAnalytics, params)
  }

  /** 新系統：取得此帳號底下所有店家的時區清單（依時區分組），label 以 SETTLEMENT_TIMEZONES 為準 */
  getAccountTimezones = async (): Promise<ApiResponse<StoreTimeZoneGroupResType[]>> => {
    const response = await this.get<unknown, ApiResponse<StoreTimeZoneGroupResType[]>>(
      StoreEnums.getAccountTimezones
    )

    if (response.result) {
      response.result = response.result.map((group) => ({
        ...group,
        label:
          SETTLEMENT_TIMEZONES.find((tz) => tz.value === group.value)?.label ??
          formatUtcOffsetLabel(group.value, group.label)
      }))
    }

    return response
  }

  /** 只取自己名下店家資訊清單 */
  getStoresUnderMyOwn = async (): Promise<ApiResponse> => {
    return await this.get<unknown, ApiResponse>(StoreEnums.storesUnderMyOwn)
  }

  /** 取得其他店主底下的可用店家資訊清單 */
  getStoresOnOtherPool = async (): Promise<ApiResponse> => {
    return await this.get<unknown, ApiResponse>(StoreEnums.storesOnOtherPool)
  }

  /** 記錄當下點選的店家 */
  recordCurrentStore = async (params: string): Promise<ApiResponse> => {
    return await this.post<string, ApiResponse>(StoreEnums.recordCurrentStore, params)
  }

  /** 建立新店家 */
  createNewStore = async (params: BaseCreateStoreReqType): Promise<ApiResponse> => {
    return await this.post<BaseCreateStoreReqType, ApiResponse>(StoreEnums.add, params)
  }

  /** 更新店家 */
  updateStore = async (params: BaseCreateStoreReqType): Promise<ApiResponse> => {
    return await this.put<BaseCreateStoreReqType, ApiResponse>(StoreEnums.update, params)
  }

  /** 變更店家群組 */
  changeStoreGroup = async (
    params: BaseChangeStoreGroupReqType,
    abortSignal?: AbortSignal
  ): Promise<ApiResponse> => {
    return await this.put<BaseChangeStoreGroupReqType, ApiResponse>(
      StoreEnums.changeStoreGroup,
      params,
      {
        signal: abortSignal
      }
    )
  }
}
